import { useId, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import {
  DownOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  LoadingOutlined,
} from '../../../components/icons'
import { DropdownMenu } from '../../../components/ui/DropdownMenu'
import { useToast } from '../../../components/ui/ToastProvider'
import type { FileToResolve } from '../staging-types'
import { ACCEPTED_DRAWING_EXTENSIONS } from '../types'

// Gap 2 fix — this is a *drawing* picker, so it must offer drawing
// extensions (`ACCEPTED_DRAWING_EXTENSIONS`), not the 3D CAD-model list
// (`ACCEPTED_CAD_EXTENSIONS`). See the comment on `ACCEPTED_DRAWING_EXTENSIONS`
// in `types.ts` — this is a product-only distinction, not a mirror of any
// server-side validation.
//
// UID-497 — the DEFAULT, now overridable per call site via `acceptExtensions`:
// the BOM-leaf control is PDF-only (`accept=".pdf"`, and its node route 415s
// anything else), while the part-scoped control keeps this wider list.

export interface DrawingAssociationControlProps {
  readonly partName: string
  readonly drawingFilename: string | null
  /** Candidate drawings to associate — the only "files" surface the
   * `StagingService` seam exposes (there is no separate drawing-catalog
   * lookup); sourced from the session's files-to-resolve list. */
  readonly candidateDrawings: readonly FileToResolve[]
  readonly onAssociateDrawing: (fileId: string) => void
  readonly onGenerateDrawing: () => Promise<unknown>
  /** UID-222 #9-upload — real presign-then-PUT flow; takes the chosen File. */
  readonly onUploadDrawing: (file: File) => Promise<unknown>
  /** Trigger text when no drawing is attached — "Add a drawing" in the flat
   * `IdentifiedPartRow`, "No drawing" in the BOM leaf's merged drawing cell
   * (UX spec `docs/design/staging-bom-hierarchy-tree-ux-spec.md` §4.1 — a
   * DESIGN-spec section, NOT an ADR clause; see the component doc below). */
  readonly emptyLabel: string
  /**
   * Accessible name for the dropdown — it names the `role="menu"` PANEL, so
   * it MUST describe what the menu's items actually do. Every item is a
   * DRAWING action (associate / generate / upload), so the name must be
   * drawing-truthful: "Associated drawing for {name}".
   *
   * ADR-0144 D5 — the BOM-leaf call site used to pass "Catalog match for
   * {name}" here, which announced a capability this control does not have
   * (there is no catalog lookup on this prop surface at all): a WCAG 2.2
   * 4.1.2 (Name, Role, Value) failure. Do not reintroduce a catalog-shaped
   * name unless a catalog-shaped ACTION lands with it (blocked on R11).
   */
  readonly menuLabel: string
  /**
   * UID-497 (T10) — DETACH ("No drawing"), the comp's own `drawOptions` entry
   * (`Fictiv Platform.dc.html:9290`: `assignDrawing(p.id,'','')`). OPTIONAL and
   * absent by default, so `IdentifiedPartRow`'s menu is byte-unchanged: a
   * part-scoped detach has no verb behind it
   * (`POST /staging/parts/{partId}/drawing` has no `detach` arm), and offering
   * a menu item that 400s would be worse than not offering it. Supplied only by
   * the custom BOM leaf, whose node verb does have one.
   */
  readonly onDetachDrawing?: () => void
  /**
   * UID-497 (T10) — narrow the file picker to PDFs. The BOM-leaf drawing
   * control is PDF-only (the design's own `accept=".pdf"`, and the node route
   * refuses anything else with a 415), while the part-scoped control keeps the
   * wider `ACCEPTED_DRAWING_EXTENSIONS` (`.pdf`/`.dwg`/`.dxf`) it shipped with.
   * Defaults to the wider list, so omitting this prop changes nothing.
   */
  readonly acceptExtensions?: readonly string[]
  /**
   * UID-560 — the drawing-name pill wants every row's pill the SAME width,
   * not shrink-to-fit (a short filename produced a visibly narrower pill
   * than a long one, even though its column is already a uniform width —
   * `<table>`'s default auto layout for `IdentifiedPartRow`'s flat "Drawing
   * / matched parts" column, and a fixed `.staging-bom-col-match` width for
   * `StagingBomLeafRow`'s "Catalog match / drawing" column). Optional and
   * defaults to the legacy shrink-to-fit sizing; both callers pass it.
   */
  readonly fullWidth?: boolean
}

/**
 * The shared drawing-association affordance (dropdown of candidate drawings +
 * generate/upload actions, hidden file input, and inline error) reused by
 * `IdentifiedPartRow` (flat "Ready to add" table) and `StagingBomLeafRow`'s
 * merged DRAWING cell (`docs/design/staging-bom-hierarchy-tree-ux-spec.md`
 * §4.1 — ADR-0144 D5/Finding A: §4.1 is a section of the product-designer's
 * UX SPEC, and was previously mis-cited here as "ADR-0086 §4.1". ADR-0086 has
 * only D1–D4 and no §4.1 at all; this repo's ADRs use D-numbers, so any
 * `§`-shaped ADR citation is suspect by construction. The mis-citation
 * promoted a design-spec section — one that itself filed an OPEN QUESTION
 * (§OQ-5) about whether the catalog states were feasible — into an immovable
 * architecture decision it never was). Extracted verbatim from
 * `IdentifiedPartRow` so the two callers can't drift — the only differences
 * are the `emptyLabel` and `menuLabel` text. Returns the control's inner
 * markup; the caller supplies the surrounding `<td>`/layout context.
 */
export function DrawingAssociationControl({
  partName,
  drawingFilename,
  candidateDrawings,
  onAssociateDrawing,
  onGenerateDrawing,
  onUploadDrawing,
  emptyLabel,
  menuLabel,
  onDetachDrawing,
  acceptExtensions,
  fullWidth,
}: DrawingAssociationControlProps) {
  const [drawingMessage, setDrawingMessage] = useState<string | null>(null)
  const [uploadBusy, setUploadBusy] = useState(false)
  // Fictiv Platform.dc.html:5968's `drawingGenId` — a single per-part
  // generating flag. This component instance is already scoped to one part
  // (see `DrawingAssociationControlProps`), so a local boolean *is* the
  // per-part id the prototype threads through session state.
  const [generating, setGenerating] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const fileInputId = useId()
  const { showToast } = useToast()

  const hasDrawing = Boolean(drawingFilename)
  /**
   * Associated-drawing trigger tone (Fictiv Platform.dc.html:7379's
   * `drawChipStyle`): teal tint once a drawing is attached, dashed/neutral
   * when none is, info/blue while a generate request is in flight (`:9316`).
   *
   * UID-402 — the amber `staging-drawing-trigger-missing` branch (applied when
   * a `custom` part had no drawing) is GONE. Per the IMTS Cleanup Bash review,
   * **a missing drawing is an acceptable state, not a problem**: orange on
   * Triage is reserved for "Needs resolution", and tinting a drawing-less part
   * amber made routine uploads read as broken. Both drawing-less cases now get
   * the same neutral dashed affordance, which still reads as "you could add
   * one" without asserting that you must.
   *
   * This also removed the component's `partType` prop — it existed solely to
   * pick that amber branch and had no other consumer.
   */
  const toneClass = generating
    ? 'staging-drawing-trigger-generating'
    : hasDrawing
      ? 'staging-drawing-trigger-has'
      : 'staging-drawing-trigger-none'

  // `:9314`'s `drawLabel: drawGen ? 'Generating…' : (curDraw || 'No drawing')`.
  const triggerLabel = generating ? 'Generating…' : (drawingFilename ?? emptyLabel)

  async function handleGenerate(close: () => void) {
    // `:9314`'s `drawMenuOpen: drawMenuOpen && !drawGen` — closing here (before
    // the request even starts) is what keeps the menu from reopening while
    // `generating` is true; the trigger below additionally refuses to
    // re-toggle it for the same reason.
    close()
    setGenerating(true)
    setDrawingMessage(null)
    // `:9321`'s `this.flash('Generating a drawing from your CAD model…')`.
    showToast('Generating a drawing from your CAD model…', 'info')
    try {
      // No CLIENT-side fabrication and no artificial delay here — whatever the
      // server does (UID-554: attaches a demo `_generated.pdf` for a
      // `type: 'custom'` identified part; still 501s for a genuinely real
      // one) is what `onGenerateDrawing` resolves/rejects with.
      // `onGenerateDrawing` (see StagingPage.tsx's wiring) deliberately routes
      // through `applyMutation`, NOT `runMutation` — the latter swallows the
      // rejection into a page-level alert, which would break this row's OWN
      // inline error message for the genuine-501 case (ADR-0066 §32-34 — a
      // real part's `generate` refusal must surface as an honest error here,
      // never silently, and never as a fabricated drawing).
      await onGenerateDrawing()
    } catch (error) {
      setDrawingMessage(
        error instanceof Error ? error.message : 'That didn’t go through. Try again.',
      )
    } finally {
      setGenerating(false)
    }
  }

  // Gap 3 (documented divergence, not a defect) — the design
  // (`Fictiv Platform.dc.html:9320`) navigates to the Uploads page for
  // "Upload a drawing…"; this opens a native file picker inline instead. That's
  // a deliberate improvement over the mock: navigating away from the Review
  // page would lose the user's in-progress triage context (selections, other
  // pending drawing/quantity edits), whereas the inline picker resolves the
  // upload without leaving the row. Behavior intentionally kept as-is.
  function handleUploadTrigger(close: () => void) {
    close()
    fileInputRef.current?.click()
  }

  async function handleFileChosen(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setUploadBusy(true)
    setDrawingMessage(null)
    try {
      await onUploadDrawing(file)
    } catch (error) {
      setDrawingMessage(error instanceof Error ? error.message : 'We couldn’t upload that drawing.')
    } finally {
      setUploadBusy(false)
    }
  }

  return (
    <>
      <DropdownMenu
        menuLabel={menuLabel}
        // UID-568 — both call sites need this: the BOM tree's clipping
        // ancestors (`.staging-bom-drawing-cell`/`.staging-bom-matched-cell`
        // `overflow: hidden`, `.staging-bom-tree-scroll` `overflow-x: auto`)
        // clip it there, and the flat table's OWN `.parts-table { overflow-x:
        // auto }` (`uploads.css`, `@media (max-width: 899px)`) clips it there
        // below the 900px breakpoint.
        escapeClipping
        {...(fullWidth ? { className: 'staging-drawing-dropdown-fill' } : {})}
        renderTrigger={({ open, toggle, triggerRef }) => (
          <button
            ref={triggerRef}
            type="button"
            className={`staging-drawing-trigger ${toneClass}${
              fullWidth ? ' staging-drawing-trigger-fill' : ''
            }`}
            aria-haspopup="menu"
            aria-expanded={generating ? false : open}
            // `:9314`'s `drawMenuOpen: drawMenuOpen && !drawGen` — the menu is
            // locked shut while generating. `aria-disabled` (not the native
            // `disabled` attribute) on purpose: a natively-disabled button is
            // pulled out of the tab order in most browsers/AT, so a screen-
            // reader or keyboard user tabbing through the row would lose all
            // sense that this control exists mid-request. `aria-disabled`
            // keeps it focusable and in the accessibility tree — AT still
            // announces it (and, via the "Generating…" label, *why* it's
            // inert) — while omitting the `onClick` handler below makes it
            // genuinely non-actionable (no toggle fires on click or
            // Enter/Space).
            aria-disabled={generating ? true : undefined}
            onClick={generating ? undefined : toggle}
          >
            {generating && <LoadingOutlined aria-hidden="true" className="staging-drawing-spin" />}
            {!generating && !hasDrawing && <ExclamationCircleOutlined aria-hidden="true" />}
            {!generating && hasDrawing && <FileTextOutlined aria-hidden="true" />}
            {/* `role="status"` (via the implicit-role `<output>`, matching
                `StagingSummaryCard`'s pattern) only while generating, so the
                "Generating…" label change is announced to AT instead of
                being conveyed by the spinner/color alone. A plain `<span>`
                the rest of the time keeps every other row's trigger from
                registering as a status/live region — there'd otherwise be
                one per row, which is both noisy for AT and would make
                `getByRole('status')` ambiguous wherever this control is
                rendered. */}
            {generating ? <output>{triggerLabel}</output> : <span>{triggerLabel}</span>}
            <DownOutlined aria-hidden="true" />
          </button>
        )}
        renderMenu={(close) => (
          <>
            <div className="dropdown-menu-header">Associate a drawing</div>
            {candidateDrawings.length === 0 && (
              <p className="dropdown-menu-empty">No uploaded files available to associate.</p>
            )}
            {candidateDrawings.map((file) => (
              <button
                key={file.fileId}
                type="button"
                role="menuitem"
                className="dropdown-menu-item"
                onClick={() => {
                  onAssociateDrawing(file.fileId)
                  close()
                }}
              >
                <FileTextOutlined aria-hidden="true" />
                <span>{file.filename}</span>
              </button>
            ))}
            {/* UID-497 — DETACH, the comp's own last `drawOptions` entry
                (`:9290`), positioned after the pool and before the
                generate/upload actions exactly as it is there. Only rendered
                when a detach verb was supplied AND there is something to
                detach: "No drawing" on a row that already has no drawing is a
                no-op dressed as an action. */}
            {onDetachDrawing !== undefined && hasDrawing && (
              <button
                type="button"
                role="menuitem"
                className="dropdown-menu-item"
                onClick={() => {
                  onDetachDrawing()
                  close()
                }}
              >
                <span>No drawing</span>
              </button>
            )}
            <div className="dropdown-menu-divider" aria-hidden="true" />
            <button
              type="button"
              role="menuitem"
              className="dropdown-menu-item dropdown-menu-item-info"
              onClick={() => void handleGenerate(close)}
            >
              Generate drawing (demo)
            </button>
            <button
              type="button"
              role="menuitem"
              className="dropdown-menu-item dropdown-menu-item-info"
              onClick={() => handleUploadTrigger(close)}
              disabled={uploadBusy}
            >
              {uploadBusy ? 'Uploading…' : 'Upload a drawing…'}
            </button>
          </>
        )}
      />
      <label className="sr-only" htmlFor={fileInputId}>
        Drawing file for {partName}
      </label>
      <input
        ref={fileInputRef}
        id={fileInputId}
        type="file"
        className="sr-only"
        accept={(acceptExtensions ?? ACCEPTED_DRAWING_EXTENSIONS).join(',')}
        onChange={(event) => void handleFileChosen(event)}
      />
      {drawingMessage && (
        <p role="alert" className="staging-row-inline-error">
          {drawingMessage}
        </p>
      )}
    </>
  )
}
